// Reading one microphone frame (issue #3518): the few pure questions both listening Learn
// exercises ask of every CaptureFrame the analysis worker delivers - how much audio time it
// accounts for, whether it is silence (with or without a voice already heard), whether the general
// detector's pitch can be trusted, and how full to draw the "Hearing you" meter. The Range Finder
// and Pitch Match each used to answer them for themselves, in the same words; here they answer
// them together, through the shared thresholds in learn_rules.ts, so the phone validation pass
// still tunes a frame's meaning in one place. Pure: no React, no DOM, no timers, no audio.

import { learnListeningTunables } from '../../../../../common/src/learn_rules.ts';
import { CaptureFrame } from './learn_mic_capture.ts';

// Elapsed audio time this frame accounts for; a gap (a backgrounded tab) counts as at most the cap,
// so time nobody was listening through can never hand out a match
export function frameDelta(lastFrameMs: number | undefined, timeMs: number): number {
  if (lastFrameMs === undefined) {
    return 0;
  }
  return Math.min(Math.max(0, timeMs - lastFrameMs), learnListeningTunables.frameDeltaCapMs);
}

// Whether this frame is the room rather than a voice. The bar to be heard at all is voicedDbfs;
// pass `heard` once a voice has been read and the bar drops to unvoicedDbfs, so a low note sung
// softly - which sits between the two on a laptop - is still the singer rather than the room
// (issue #3518, Michael's low F). Pitch Match asks the `heard` form while it is reading a singer,
// and keeps a voice it is still reading surely below even that (the held voice, note_scorer.ts:
// a phone that turns a held note down 30 dB, issue #3708); the Range Finder only ever asks the
// entry question, so it calls this with one argument.
export function isSilent(frame: CaptureFrame, heard = false): boolean {
  const { voicedDbfs, unvoicedDbfs } = learnListeningTunables;
  return frame.dbfs < (heard ? unvoicedDbfs : voicedDbfs);
}

// The general detector's (YIN's) reading when it is confident enough to be trusted, else nothing
export function confidentPitch(frame: CaptureFrame): { frequency: number; clarity: number } | undefined {
  return frame.pitch && frame.pitch.clarity >= learnListeningTunables.voicedClarity ? frame.pitch : undefined;
}

// The "Hearing you" meter: -60 dBFS and below is empty, -20 dBFS and above is full
export function levelForDbfs(dbfs: number): number {
  return Math.min(1, Math.max(0, (dbfs + 60) / 40));
}
